import { getLetterFields } from '../generator/config';
import { Field, LetterTypes, Sources } from '../generator/types';

import {
  getOrderedSources,
  resolveBlockSet,
  resolveParagraphs,
} from './blockResolver';
import { readFieldValue } from './fieldKeys';
import { PricingMode } from './types';

// Reserved markers are resolved from case data, not from user input.
const RESERVED_FIELDS = new Set(['riders', 'carrierDisplayName']);

export interface UnfilledField {
  key: string;
  placeholder: string;
  blockId: Sources;
  blockLabel: string;
  // Reason the field is flagged. 'empty' for missing values, or the yup
  // schema's error message when a non-empty value fails validation (e.g.
  // 'Height must be in the format X\'Y"'). Banner uses this to tell the user
  // WHY a field that visually looks filled is still being flagged.
  reason: 'empty' | string;
}

export interface UnfilledFieldsInput {
  letterType: LetterTypes | null;
  pricingMode: PricingMode;
  selectedSources: Sources[];
  isJuvenile: boolean;
  fieldValues: Record<string, string>;
}

// Drawer stores OTAF higher and OTAF same-price under the same letterType
// (LetterTypes.otaf + pricingMode). Legacy config keys the two by distinct
// LetterTypes values, so map back when looking up field schemas.
function resolveLegacyLetterType(
  letterType: LetterTypes,
  pricingMode: PricingMode,
): LetterTypes {
  if (letterType === LetterTypes.otaf && pricingMode === 'same') {
    return LetterTypes.otafSamePrice;
  }
  return letterType;
}

function buildSchemaIndex(
  letterType: LetterTypes,
  pricingMode: PricingMode,
  sourceId: Sources,
): Map<string, Field['schema']> {
  const legacyType = resolveLegacyLetterType(letterType, pricingMode);
  const fields = getLetterFields(legacyType, sourceId);
  const map = new Map<string, Field['schema']>();
  for (const f of fields) map.set(f.name, f.schema);
  return map;
}

export function getUnfilledFields(input: UnfilledFieldsInput): UnfilledField[] {
  const out: UnfilledField[] = [];
  const seen = new Set<string>();
  const blockSet = resolveBlockSet(input.letterType, input.pricingMode);
  const orderedIds = getOrderedSources(blockSet, input.selectedSources);

  for (const id of orderedIds) {
    const block = blockSet[id];
    if (!block) continue;
    // Per-source legacy schemas — reuses the exact yup rules the legacy form
    // applied (e.g. heightSchema requires X'Y", weightSchema requires positive).
    const schemas = input.letterType
      ? buildSchemaIndex(input.letterType, input.pricingMode, id)
      : new Map<string, Field['schema']>();

    const paragraphs = resolveParagraphs(block, input.isJuvenile);
    paragraphs.forEach((p, _paraIdx) => {
      for (const f of p.fields) {
        if (RESERVED_FIELDS.has(f.key)) continue;
        // User explicitly deleted this field span from the paragraph — skip validation.
        if (input.fieldValues[`__field_removed_${id}_${f.key}`] === '1')
          continue;

        const value = readFieldValue(input.fieldValues, f.key, id);
        const isEmpty = !value || value.trim() === '';
        const schema = schemas.get(f.key);
        // Flag the field when it's empty OR when a legacy schema rejects the value.
        let reason: 'empty' | string | null = null;
        if (isEmpty) {
          reason = 'empty';
        } else if (schema !== undefined) {
          try {
            schema.validateSync(value);
          } catch (err) {
            // yup throws ValidationError; .message holds the human-readable text.
            const msg =
              err && typeof err === 'object' && 'message' in err
                ? String((err as { message: unknown }).message)
                : 'Invalid value';
            reason = msg;
          }
        }
        if (reason === null) continue;

        // Values are stored per block, so the same field in two blocks is two
        // separate things to fill. Dedupe only repeats within one block (the
        // same field in several paragraphs of that block).
        const seenKey = `${id}:${f.key}`;
        if (seen.has(seenKey)) continue;
        seen.add(seenKey);
        out.push({
          key: f.key,
          placeholder: f.placeholder,
          blockId: id,
          blockLabel: block.label,
          reason,
        });
      }
    });
  }
  return out;
}

export function isFieldEmpty(value: string | undefined): boolean {
  return !value || value.trim() === '';
}
